import PeoplesIcon from "@rsuite/icons/Peoples";
import CouponIcon from "@rsuite/icons/Coupon";
import GridIcon from "@rsuite/icons/Grid";
import BlockIcon from "@rsuite/icons/Block";
import CalendarIcon from "@rsuite/icons/Calendar";
import dayjs from "dayjs";
import { formatCurrency } from "../../constants/utils";
import { BREAKDOWN_HEIGHTS, DAILY_RATES_STATUS } from "./constants";

export const UI_LABELS = {
  TRAVEL_BUYER: "Travel Buyer",
};

export const searchFields = {
  lastName: {
    label: "Last Name",
    icon: PeoplesIcon,
  },
  firstName: {
    label: "First Name",
    icon: PeoplesIcon,
  },
  htConfirmationCode: {
    label: "HT Confirmation Code",
    icon: GridIcon,
  },
  pmsId: {
    label: "PMS ID",
    icon: CouponIcon,
  },
  sourceRoomConfirmationNumber: {
    label: "CRS ID",
    icon: CouponIcon,
  },
  cancelledReservation: {
    label: "Cancelled Reservation",
    icon: BlockIcon,
  },
  checkIn: {
    label: "Check In Dates",
    icon: CalendarIcon,
  },
  checkOut: {
    label: "Check Out Dates",
    icon: CalendarIcon,
  },
  bookingDate: {
    label: "Booking Dates",
    icon: CalendarIcon,
  },
};

export const cancelledData = [
  "Exclude cancelled",
  "Only cancelled",
  "Include cancelled",
].map((item, idx) => ({
  label: item,
  value: `${idx}`,
}));

export const DEFAULT_PAGE_SIZE = 10;

export const INIT_FORM_VALUE = {
  lastName: "",
  firstName: "",
  htConfirmationCode: "",
  pmsId: "",
  sourceRoomConfirmationNumber: "",
  cancelledReservation: null,
  checkIn: null,
  checkOut: null,
  bookingDate: null,
};

// EO-1193: ht-supplier-backend (ReservationServiceImpl.enrichWithDerivedRateNames)
// sends every distinct promo name of a meshed booking joined with this separator.
export const PROMO_RATE_PLAN_SEPARATOR = ", ";

/**
 * Splits the Promo Rate Plan cell value into one entry per promo.
 * Assumes promo names never contain the separator themselves; if one does,
 * it shows as two entries in the cell (the export keeps the raw string).
 */
export const splitPromoRatePlans = (value) =>
  value ? String(value).split(PROMO_RATE_PLAN_SEPARATOR) : [];

const createColumnDef = (dataKey, exportKey, header, label, width = 150) => ({
  dataKey,
  exportKey,
  header,
  label: label || header,
  width,
  isHidden: false,
});

export const DEFAULT_COLUMNS = [
  createColumnDef("status", "includeStatus", "Status"),
  createColumnDef(
    "demandClient",
    "includeDemandClient",
    UI_LABELS.TRAVEL_BUYER
  ),
  createColumnDef("htConfirmationCode", "includeHtConfirmationId", "Conf ID"),
  createColumnDef("pmsId", "includePmsId", "PMS ID", null, 100),
  createColumnDef("cxlId", "includeCxlId", "CXL ID", null, 100),
  createColumnDef("rateplan", "includeRateplan", "Rate Plan", null, 100),
  createColumnDef(
    "promoRatePlan",
    "includePromoRateplan",
    "Promo Rate Plan",
    null,
    240
  ),
  createColumnDef("meshed", "includeMeshed", "Meshed", null, 90),
  createColumnDef("guestName", "includeGuestName", "Guest Name"),
  createColumnDef("roomType", "includeRoomType", "Room type", null, 180),
  createColumnDef("arrival", "includeArrivalDate", "Arrival", "Arrival Date"),
  createColumnDef(
    "departure",
    "includeDepartureDate",
    "Departure",
    "Departure Date"
  ),
  createColumnDef(
    "noOfGuests",
    "includeNoOfGuests",
    "Number of guests",
    null,
    180
  ),
  createColumnDef("payableTotal", "includePayableTotal", "Payable total"),
  createColumnDef("payable", "includePayable", "Payable"),
  createColumnDef("tax", "includeTax", "Tax", null, 100),
  createColumnDef(
    "bookingCreationDate",
    "includeBookingCreationDate",
    "Booking Creation Date",
    null,
    200
  ),
  createColumnDef("currency", "includeCurrency", "Currency"),
  createColumnDef("repushVcc", undefined, "Repush VCC"),
];

export const sumDailyRates = (rates, key) =>
  rates.reduce((sum, rate) => sum + (Number(rate?.[key]) || 0), 0);

export const formatDailyRateAmount = (value, currency) =>
  formatCurrency(Number(value || 0).toFixed(2), currency);

// The backend often stores no display name for a tax line — the short code
// ("Sale", "OCC", "RES20") is the only label available. Fees are flagged so
// suppliers can tell them apart from taxes.
export const getPropertyTaxLabel = (tax) => {
  const label = tax?.name || tax?.code || "Tax";
  const isFee = String(tax?.taxType).toUpperCase() === "FEE";
  return isFee ? `${label} (Fee)` : label;
};

// Aggregates the nightly tax breakups into stay-level footer lines, split by
// where they are collected. Nights that carry no breakup detail still have a
// payableTax amount — those roll into one generic pay-at-booking line so the
// footer always reconciles with the nightly figures.
export const buildDailyBreakdownSummary = (rates) => {
  const lineMap = new Map();
  let untaggedTaxTotal = 0;

  rates.forEach((rate) => {
    const propertyTaxes = rate?.taxBreakup?.propertyTaxes;
    if (propertyTaxes?.length) {
      propertyTaxes.forEach((tax) => {
        const payAtProperty = Boolean(tax?.payAtProperty);
        const key = `${tax?.code || tax?.name || "tax"}|${payAtProperty}`;
        const amount = Number(tax?.taxAmount) || 0;
        const existing = lineMap.get(key);
        if (existing) {
          existing.amount += amount;
        } else {
          lineMap.set(key, {
            // The grouping key rides along as the stable React key: labels
            // alone can collide (two tax codes sharing a display name).
            key,
            label: getPropertyTaxLabel(tax),
            amount,
            payAtProperty,
          });
        }
      });
    } else {
      untaggedTaxTotal += Number(rate?.payableTax) || 0;
    }
  });

  const lines = [...lineMap.values()];
  if (untaggedTaxTotal > 0) {
    lines.push({
      key: "untagged|false",
      label: "Taxes & fees",
      amount: untaggedTaxTotal,
      payAtProperty: false,
    });
  }

  const sumLines = (taxLines) =>
    taxLines.reduce((sum, line) => sum + line.amount, 0);
  const bookingLines = lines.filter((line) => !line.payAtProperty);
  const propertyLines = lines.filter((line) => line.payAtProperty);
  const netTotal = sumDailyRates(rates, "payableNetPrice");
  const bookingTaxTotal = sumLines(bookingLines);
  const propertyTaxTotal = sumLines(propertyLines);

  return {
    bookingLines,
    propertyLines,
    netTotal,
    payAtBookingTotal: netTotal + bookingTaxTotal,
    payAtPropertyTotal: propertyTaxTotal,
    grandTotal: netTotal + bookingTaxTotal + propertyTaxTotal,
  };
};

// rsuite needs the expanded-row height before the rates arrive, so estimate
// one row per night from the stay dates plus a typical footer, then switch to
// the real counts once the daily rates are loaded. Anything beyond `max`
// scrolls inside the row.
export const calculateDailyBreakdownHeight = (rowData, dailyRatesEntry) => {
  const {
    header,
    tableHeader,
    dailyRow,
    footerRow,
    estimatedFooterRows,
    min,
    max,
    bottomGap,
  } = BREAKDOWN_HEIGHTS;
  let nights = dayjs(rowData?.departure).diff(dayjs(rowData?.arrival), "day");
  let footerRows = estimatedFooterRows;
  if (dailyRatesEntry?.status === DAILY_RATES_STATUS.LOADED) {
    nights = dailyRatesEntry.rates.length;
    const { bookingLines, propertyLines } = buildDailyBreakdownSummary(
      dailyRatesEntry.rates
    );
    // Net Price + tax/fee lines + Total + the two payable-where rows.
    footerRows = 4 + bookingLines.length + propertyLines.length;
  }
  // The gap rides OUTSIDE the clamp so the card always keeps its spacing to
  // the next row, whether the content hit the floor or the ceiling.
  if (!Number.isFinite(nights) || nights < 1) {
    return min + bottomGap;
  }
  const total =
    header + tableHeader + nights * dailyRow + footerRows * footerRow;
  return Math.min(max, Math.max(min, total)) + bottomGap;
};

export const validateIdField = (value, originalValue, dataKey) => {
  const fieldName = dataKey === "pmsId" ? "PMS ID" : "CXL ID";
  const wasEmpty = !originalValue || originalValue.trim() === "";

  if (!wasEmpty && (!value || value.trim() === "")) {
    return `${fieldName} cannot be left blank. Please add a value before saving.`;
  }

  const invalidChars = /[^a-zA-Z0-9\s\-_]/;
  if (value && invalidChars.test(value)) {
    return `${fieldName} contains invalid characters. Only letters, numbers, hyphens, underscores, and spaces are allowed.`;
  }

  return null;
};

export const ALL_COLUMNS = [
  {
    dataKey: "serialNumber",
    header: "#",
    label: "Serial No",
    width: 50,
    isHidden: true,
  },
  ...DEFAULT_COLUMNS,
  createColumnDef(
    "lengthOfStay",
    "includeLengthOfStay",
    "LOS",
    "Length of stay",
    70
  ),
  createColumnDef("lt", "includeLt", "LT", "Lead Time", 70),
  createColumnDef(
    "bookingCreationTime",
    "includeBookingCreationTime",
    "Booking Creation Time",
    null,
    230
  ),
  createColumnDef(
    "cancellationCutOffLocal",
    "includeCancellationCutOffLocal",
    "Cancellation cut-off",
    null,
    230
  ),
  createColumnDef(
    "payablePaymentType",
    "includePayablePaymentType",
    "Payable Payment Type",
    null,
    200
  ),
  createColumnDef(
    "cancellationDate",
    "includeCancellationDate",
    "Cancellation date & time",
    null,
    180
  ),
  createColumnDef("paymentPolicy", "includePaymentPolicy", "Payment policy"),
  createColumnDef(
    "sourceRoomConfirmationNumber",
    "includeSourceRoomConfirmationNumber",
    "CRS ID"
  ),
];
